import React, { useState } from "react";
import { Container } from "../container";
import axios from "axios";

type FormData = {
  cancelButtonText?: string | null;
  hasCancelButton?: boolean | null;
  headerAuthKey?: string | null;
  headerAuthToken?: string | null;
  name?: string | null;
  postURL?: string | null;
  recipient?: string | null;
  submitButtonText?: string | null;
  id: string;
  successMessageOverride?: string | null;
  errorMessageOverride?: string | null;
  formElements?:
    | ({
        altText?: string | null;
        id?: number | null;
        label?: string | null;
        name?: string | null;
        type?: string | null;
        defaultValue?: string | null;
        placeholder?: string | null;
        tooltip?: string | null;
        required?: boolean | null;
      } | null)[]
    | null;
};

export const Form: React.FC<FormData> = ({
  cancelButtonText,
  hasCancelButton,
  headerAuthKey,
  headerAuthToken,
  name,
  postURL,
  recipient,
  submitButtonText,
  successMessageOverride,
  errorMessageOverride,
  id,
  formElements,
  ...props
}) => {
  const getInitialData = () =>
    formElements
      ? formElements.reduce(
          (acc, val) => {
            if (!val || !val.name) return acc;
            acc[val.name] = val.defaultValue || "";
            return acc;
          },
          {} as { [key: string]: string | null | undefined },
        )
      : {};
  const reset = () => {
    setData(getInitialData());
    setMessage(null);
    setStatus("idle");
  };
  const [data, setData] = useState(getInitialData());
  const [status, setStatus] = useState<
    "idle" | "pending" | "success" | "error"
  >("idle");
  const [message, setMessage] = useState<string | null>(null);
  const validate = () => {
    if (!formElements) return true;
    const elements = formElements.filter((element) =>
      element ? element.required : false,
    );
    for (const element of elements) {
      if (!element?.name) return false;
      if (!data[element?.name]) return false;
    }
    return true;
  };
  const post = () => {
    console.log("posting form", postURL, postURL?.trim());
    // Make sure the name is correct when posting to N8N
    if (!postURL || postURL.trim() == "")
      postURL = `https://n8n.li-ma.nl/webhook/form?name=${name}`;
    if (!validate()) {
      setStatus("error");
      setMessage("please fill out all required fields");
      return;
    }
    setStatus("pending");
    axios({
      url: postURL,
      method: "POST",
      headers: {
        [headerAuthKey || "foo"]: headerAuthToken,
        "Content-Type": "application/json",
      },
      data,
    })
      .then((response) => {
        // console.log("success", response);
        setData(getInitialData);
        setStatus("success");
        try {
          setMessage(
            successMessageOverride
              ? successMessageOverride
              : response.data.message,
          );
        } catch {
          setMessage("success: " + JSON.stringify(response.data));
        }
      })
      .catch((error) => {
        // console.log("error", error);
        setStatus("error");
        try {
          setMessage(
            errorMessageOverride
              ? errorMessageOverride
              : error.response.data.message,
          );
        } catch {
          setMessage("error: " + JSON.stringify(error.response.data));
        }
      });
  };

  return (
    <Container {...props} className="mt-5 grid grid-cols-4 gap-16">
      <div className="hidden lg:col-span-1  lg:flex"> </div>
      <div className="col-span-4 lg:col-span-2">
        <div>
          {formElements &&
            formElements.map((element) => {
              if (!element || !element.name) return null;
              const elementId = `${id}-${element.id}`;
              return (
                <div key={`form-element-${elementId}`}>
                  {element.type !== "hidden" && (
                    <div className="mb-2">
                      <label className="text-m font-bold" htmlFor={elementId}>
                        {element.label} {element.required && "*"}
                      </label>
                    </div>
                  )}
                  <div className={`${element.type === "hidden" ? "" : "mb-4"}`}>
                    {[
                      "short_text",
                      "email",
                      "url",
                      "number",
                      "date",
                      "hidden",
                    ].includes(element.type || "") && (
                      <input
                        className="w-full border border-beige px-2 py-2"
                        type={
                          !element.type || element.type === "short_text"
                            ? "text"
                            : element.type
                        }
                        name={element.name || undefined}
                        alt={element.altText || undefined}
                        id={elementId}
                        key={elementId}
                        placeholder={element.placeholder || undefined}
                        value={data[element.name] || ""}
                        onChange={(e) =>
                          setData((data) => ({
                            ...data,
                            [element.name as string]: e.target.value || "",
                          }))
                        }
                      />
                    )}
                    {element.type === "long_text" && (
                      <textarea
                        className="w-full border border-beige px-2 py-2"
                        name={element.name || ""}
                        rows={15}
                        id={elementId}
                        key={elementId}
                        placeholder={element.placeholder || undefined}
                        value={data[element.name] || ""}
                        onChange={(e) =>
                          setData((data) => ({
                            ...data,
                            [element.name as string]: e.target.value || "",
                          }))
                        }
                      />
                    )}
                  </div>
                </div>
              );
            })}
          <div className="flex justify-end">
            {hasCancelButton && (
              <button
                onClick={() => reset()}
                className="text-md relative -left-1 mr-3 rounded-lg border border-beige bg-white px-3 py-1 font-bold lg:rounded-2xl lg:px-10 lg:py-2 lg:text-lg"
              >
                {cancelButtonText}
              </button>
            )}
            <button
              onClick={() => post()}
              disabled={status === "pending"}
              className={`
                text-md
                relative
                -left-1
                rounded-lg
                border
                border-beige
                bg-white
                px-3
                py-1
                font-bold
                lg:rounded-2xl
                lg:px-10
                lg:py-2
                lg:text-lg
                ${status === "pending" ? "opacity-30" : ""}
              `}
            >
              {submitButtonText}
            </button>
          </div>
          {(status === "error" || status === "success") && (
            <div className="mt-3 flex justify-end">
              {message && (
                <div
                  className={`${status === "error" ? "text-[#ff0000]" : ""}`}
                >
                  {message}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};
